import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Footer from './Footer';
import GlobalSearch from './GlobalSearch';
import ProfileModal from './ProfileModal';
import SettingsModal from './SettingsModal';
import NotificationDropdown from './NotificationDropdown';
import { useNotifications } from '../context/NotificationsContext';

const NavIcon = ({ d }) => (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
  </svg>
);

const navItemsIcons = {
  dashboard: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6z M14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6z',
  simulations: 'M13 10V3L4 14h7v7l9-11h-7z',
  assessment: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  organization: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
  history: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
};

function getInitials(name) {
  if (!name || !name.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();

  const navItemsMap = {
    dashboard: { label: t.dashboard, icon: navItemsIcons.dashboard },
    simulations: { label: t.simulations, icon: navItemsIcons.simulations },
    assessment: { label: t.assessment, icon: navItemsIcons.assessment },
    organization: { label: t.organization, icon: navItemsIcons.organization },
    history: { label: t.history, icon: navItemsIcons.history },
  };
  const navigate = useNavigate();
  const orgId = user?.organizationId;
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    setProfileOpen(false);
    logout();
    navigate('/login');
  };

  const navItems = orgId
    ? [
        { to: `/org/${orgId}/dashboard`, key: 'dashboard' },
        { to: `/org/${orgId}/simulations`, key: 'simulations' },
        { to: `/org/${orgId}/assessment`, key: 'assessment' },
        { to: `/org/${orgId}/organization`, key: 'organization' },
        { to: `/org/${orgId}/history`, key: 'history' },
      ]
    : [{ to: '/organizations/new', key: 'new-org', label: t.newOrganization, icon: null }];

  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const notificationRef = useRef(null);
  const { unreadCount } = useNotifications();

  useEffect(() => {
    function handleNotifClickOutside(e) {
      if (notificationRef.current && !notificationRef.current.contains(e.target)) {
        setNotificationOpen(false);
      }
    }
    document.addEventListener('mousedown', handleNotifClickOutside);
    return () => document.removeEventListener('mousedown', handleNotifClickOutside);
  }, []);

  const handleResetDemo = () => {
    setSettingsModalOpen(false);
    window.location.reload();
  };
  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc] dark:bg-slate-900">
      <header
        className="relative z-40 overflow-visible bg-white dark:bg-slate-900 border-b border-[#e2e8f0] dark:border-slate-700"
        style={{ height: '64px' }}
      >
        <div className="relative w-full max-w-[1600px] mx-auto h-full px-4 sm:px-6 flex items-center">
          <div className="flex items-center flex-1 min-w-0 justify-start">
            <Link
              to={orgId ? `/org/${orgId}/dashboard` : '/'}
              className="flex items-center gap-2"
            >
              <svg className="w-7 h-7 text-[#0ea5e9]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L4 6v6c0 5.5 4 10.5 8 12 4-1.5 8-6.5 8-12V6l-8-4z" />
              </svg>
              <span className="font-bold text-lg text-[#0f172a] dark:text-slate-100 hidden sm:inline">SecureCoop</span>
            </Link>
          </div>

          <nav className="hidden lg:flex items-center gap-4 flex-shrink-0 justify-center px-8">
            {navItems.map((item) => {
              const meta = navItemsMap[item.key] || { label: item.label || t.menu, icon: item.icon };
              const active =
                location.pathname === item.to ||
                location.pathname.startsWith(item.to + '/') ||
                (item.key === 'history' && location.pathname.includes('/report'));
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center gap-2 py-2.5 px-3 rounded-[10px] text-sm font-medium transition duration-200 ${
                    active
                      ? 'bg-[#0ea5e9]/10 text-[#0ea5e9] dark:text-[#0ea5e9] font-semibold'
                      : 'text-[#475569] dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/40 hover:text-[#0f172a] dark:hover:text-slate-100'
                  }`}
                >
                  {meta.icon && <NavIcon d={meta.icon} />}
                  {meta.label || item.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center justify-end flex-1 min-w-0 gap-4">
            {orgId ? (
              <GlobalSearch orgId={orgId} expandable expandLeft />
            ) : (
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 text-muted border border-slate-200 dark:border-slate-700" aria-hidden>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            )}

            <div className="relative" ref={notificationRef}>
              <button
                onClick={() => setNotificationOpen(!notificationOpen)}
                className="relative flex items-center justify-center w-10 h-10 rounded-[10px] bg-slate-50 dark:bg-slate-800 border border-[#e2e8f0] dark:border-slate-700 hover:border-[#0ea5e9]/40 text-[#475569] dark:text-slate-400 hover:text-[#0f172a] dark:hover:text-slate-100 transition duration-200"
                aria-label={t.notifications}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#0ea5e9] text-white text-[10px] font-bold">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              <NotificationDropdown isOpen={notificationOpen} onClose={() => setNotificationOpen(false)} />
            </div>

            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                className="flex items-center gap-2 py-1.5 pl-1 pr-3 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150"
              >
                <div className="w-9 h-9 rounded-full bg-[#0ea5e9] flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 overflow-hidden">
                  {user?.profilePhoto ? (
                    <img src={user.profilePhoto} alt="" className="w-full h-full object-cover" />
                  ) : (
                    getInitials(user?.name)
                  )}
                </div>
                <span className="hidden sm:inline text-sm font-medium text-[#0f172a] dark:text-slate-100 max-w-[120px] truncate">
                  {user?.name || t.user}
                </span>
                <svg
                  className={`w-4 h-4 text-slate-500 dark:text-slate-400 flex-shrink-0 transition-transform duration-200 ${profileOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {profileOpen && (
                <div className="absolute right-0 mt-2 w-48 py-1 bg-white dark:bg-slate-800 border border-[#e2e8f0] dark:border-slate-700 rounded-[12px] shadow-[0_4px_12px_rgba(0,0,0,0.05)] overflow-hidden z-50 animate-fade-in-up">
                  <div className="px-4 py-3 border-b border-[#e2e8f0] dark:border-slate-700">
                    <p className="text-sm font-medium text-title truncate">{user?.name}</p>
                    <p className="text-xs text-muted truncate">{user?.email}</p>
                  </div>
                  <button
                    onClick={() => { setProfileOpen(false); setProfileModalOpen(true); }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/40 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
                  >
                    <NavIcon d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    {t.profile}
                  </button>
                  <button
                    onClick={() => { setProfileOpen(false); setSettingsModalOpen(true); }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/40 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
                  >
                    <NavIcon d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    {t.settings}
                  </button>
                  <div className="border-t border-[#e2e8f0] dark:border-slate-700">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm text-red-400 hover:bg-red-500/15 transition-colors"
                    >
                      <NavIcon d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      {t.logout}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full text-[#0f172a] dark:text-slate-100">
        {children}
      </main>
      <Footer />

      <ProfileModal isOpen={profileModalOpen} onClose={() => setProfileModalOpen(false)} />
      <SettingsModal
        isOpen={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        onResetDemo={handleResetDemo}
      />
    </div>
  );
}
